import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "../api";
import { Button } from "../components/ui/button";
import { Calendar, ChevronLeft, ChevronRight, MapPin, Clock, FileText, X, AlertTriangle, Star, Receipt } from "lucide-react";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { useConfirm } from "../components/ConfirmProvider";
import LegalLinksCard from "../components/LegalLinksCard";

const MONTHS_FR = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const DAYS_FR = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

/** Builds and saves the main coach invoice as a PDF. Applies VAT only if the
 *  MNS opted-in (`mns_profile.vat_subject`) and uses the platform's PNG logo. */
async function generateInvoicePDF(bookingId) {
  let inv;
  try {
    const { data } = await api.get(`/invoices/booking/${bookingId}`);
    inv = data;
  } catch (e) {
    toast.error(e.response?.data?.detail || "Facture indisponible pour le moment");
    return;
  }
  const a = inv.amounts || {};
  const doc = new jsPDF();
  const W = 210;
  // Header
  doc.setFillColor(3, 105, 161); doc.rect(0, 0, W, 30, "F");
  try { doc.addImage(`${window.location.origin}/msl-logo.png`, "PNG", 165, 4, 22, 22); }
  catch (e) { console.warn("invoice logo add failed", e?.message); }
  doc.setTextColor(255); doc.setFontSize(20); doc.setFont("helvetica", "bold");
  doc.text("FACTURE", 14, 18);
  doc.setFontSize(10); doc.setFont("helvetica", "normal");
  doc.text("MySwimmingLessons", 14, 25);
  doc.setTextColor(0);
  doc.setFontSize(11); doc.setFont("helvetica", "bold");
  doc.text(`N° ${inv.invoice_number}`, 14, 42);
  doc.setFont("helvetica", "normal"); doc.setFontSize(9);
  doc.text(`Date d'émission : ${new Date(inv.issued_at).toLocaleDateString("fr-FR")}`, 14, 48);
  doc.text(`Date du cours : ${new Date(inv.booking.start_dt).toLocaleString("fr-FR")}`, 14, 53);
  // E-invoicing mandate: operation type (B2B/B2C/mixte)
  const opType = inv.operation_type || "B2C";
  doc.setFont("helvetica", "bold");
  doc.text(`Type d'opération : ${opType}`, 130, 48);
  doc.setFont("helvetica", "normal");
  // Émetteur (MNS) — full address + SIRET
  const ms = inv.mns_settings || {};
  doc.setFont("helvetica", "bold"); doc.text("ÉMETTEUR (Prestataire)", 14, 64);
  doc.setFont("helvetica", "normal");
  doc.text(`${inv.mns?.name || ""}`, 14, 70);
  doc.text(`${ms.address || ""}`, 14, 75);
  doc.text(`${ms.postal_code || ""} ${ms.city || ""}`.trim(), 14, 80);
  doc.text(`${inv.mns?.email || ""}`, 14, 85);
  doc.text(`${ms.phone || inv.mns?.phone || ""}`, 14, 90);
  if (inv.mns_profile?.siret) doc.text(`SIRET : ${inv.mns_profile.siret}`, 14, 95);
  // Client — full address (B2B shows company name + SIREN)
  const cs = inv.client_settings || {};
  const isBusiness = !!cs.is_business;
  doc.setFont("helvetica", "bold"); doc.text("DESTINATAIRE (Client)", 110, 64);
  doc.setFont("helvetica", "normal");
  doc.text(isBusiness && cs.company_name ? cs.company_name : (inv.client?.name || ""), 110, 70);
  doc.text(`${cs.address || ""}`, 110, 75);
  doc.text(`${cs.postal_code || ""} ${cs.city || ""}`.trim(), 110, 80);
  doc.text(`${inv.client?.email || ""}`, 110, 85);
  doc.text(`${cs.phone || inv.client?.phone || ""}`, 110, 90);
  if (isBusiness && cs.siren) doc.text(`SIREN : ${cs.siren}`, 110, 95);
  if (isBusiness && cs.vat_number) doc.text(`TVA : ${cs.vat_number}`, 110, 100);
  // Tableau prestation
  let y = 110;
  doc.setDrawColor(200); doc.setFillColor(240, 249, 255);
  doc.rect(14, y - 5, 182, 9, "F");
  doc.setFont("helvetica", "bold");
  doc.text("Désignation", 16, y);
  doc.text("Durée", 110, y);
  if (a.vat_subject) {
    doc.text("HT", 132, y);
    doc.text("TVA", 152, y);
    doc.text("TTC", 175, y);
  } else {
    doc.text("Tarif", 140, y);
    doc.text("Total", 175, y);
  }
  doc.setFont("helvetica", "normal");
  y += 9;
  doc.text(`Cours particulier de natation`, 16, y);
  doc.text(`${inv.booking.hours} h`, 110, y);
  if (a.vat_subject) {
    doc.text(`${a.ht.toFixed(2)} €`, 132, y);
    doc.text(`${a.tva.toFixed(2)} €`, 152, y);
    doc.text(`${a.ttc.toFixed(2)} €`, 175, y);
  } else {
    doc.text(`${a.ttc.toFixed(2)} €`, 140, y);
    doc.text(`${a.ttc.toFixed(2)} €`, 175, y);
  }
  y += 10;
  doc.setFont("helvetica", "bold"); doc.setFontSize(11);
  if (a.vat_subject) {
    doc.text(`Total HT :`, 140, y); doc.text(`${a.ht.toFixed(2)} €`, 195, y, { align: "right" }); y += 6;
    doc.text(`TVA (${a.vat_rate}%) :`, 140, y); doc.text(`${a.tva.toFixed(2)} €`, 195, y, { align: "right" }); y += 6;
    doc.text(`Total TTC :`, 140, y); doc.text(`${a.ttc.toFixed(2)} €`, 195, y, { align: "right" });
  } else {
    doc.text(`TOTAL :`, 140, y); doc.text(`${a.ttc.toFixed(2)} €`, 195, y, { align: "right" });
  }
  doc.setFont("helvetica", "normal"); doc.setFontSize(8);
  y += 10;
  if (!a.vat_subject) {
    doc.text("TVA non applicable, art. 293 B du CGI.", 14, y);
    y += 6;
  }
  // Mode paiement
  doc.setFont("helvetica", "bold"); doc.text("Paiement", 14, y); y += 5;
  doc.setFont("helvetica", "normal");
  doc.text(`Mode : ${inv.booking.payment_method === "online" ? "En ligne (Stripe)" : "Espèces — réglé directement au prestataire"}`, 14, y); y += 5;
  doc.text(`Statut : ${inv.booking.payment_status}`, 14, y); y += 5;
  doc.text(`Frais de service MySwimmingLessons (10%) : facturés séparément.`, 14, y); y += 10;
  // Mentions légales (e-invoicing compliance)
  doc.setFontSize(7); doc.setTextColor(100);
  doc.text("Mentions légales :", 14, y); y += 4;
  doc.text("• Plateforme de mise en relation : MySwimmingLessons SAS — contact@myswimminglessons.fr", 14, y); y += 3.5;
  doc.text("• Facture électronique émise en application de l'article 289 bis du CGI (réforme facturation électronique 2026).", 14, y); y += 3.5;
  doc.text(`• Type d'opération : ${opType}. Option TVA sur les débits : ${inv.mns_profile?.vat_option_debits ? "Oui" : "Non — exigible à l'encaissement"}.`, 14, y); y += 3.5;
  doc.text("• En cas de retard de paiement : pénalités de 3 fois le taux d'intérêt légal en vigueur (L.441-10 C. com.).", 14, y); y += 3.5;
  doc.text("• Indemnité forfaitaire pour frais de recouvrement : 40 €.", 14, y); y += 3.5;
  doc.text("• Pas d'escompte pour paiement anticipé.", 14, y); y += 3.5;
  doc.text("• Annulation moins de 24h avant le cours : pénalité de 30 % du tarif (cf. CGV).", 14, y); y += 3.5;
  doc.text("• Données personnelles traitées conformément au RGPD ; droits d'accès/rectification : contact@myswimminglessons.fr.", 14, y);
  doc.save(`${inv.invoice_number}.pdf`);
}

/** Builds and saves the platform-commission invoice (10% issued by MySwimmingLessons SAS to the client, TVA 20%). */
async function generateCommissionInvoicePDF(bookingId) {
  let inv;
  try {
    const { data } = await api.get(`/invoices/booking/${bookingId}/commission`);
    inv = data;
  } catch (e) {
    toast.error(e.response?.data?.detail || "Facture commission indisponible");
    return;
  }
  const a = inv.amounts || {};
  const p = inv.platform || {};
  const cs = inv.client_settings || {};
  const doc = new jsPDF();
  const W = 210;
  doc.setFillColor(3, 105, 161); doc.rect(0, 0, W, 30, "F");
  try { doc.addImage(`${window.location.origin}/msl-logo.png`, "PNG", 165, 4, 22, 22); }
  catch (e) { console.warn("commission invoice logo add failed", e?.message); }
  doc.setTextColor(255); doc.setFontSize(18); doc.setFont("helvetica", "bold");
  doc.text("FACTURE — Frais de service", 14, 18);
  doc.setFontSize(10); doc.setFont("helvetica", "normal");
  doc.text("MySwimmingLessons", 14, 25);
  doc.setTextColor(0);
  doc.setFontSize(11); doc.setFont("helvetica", "bold");
  doc.text(`N° ${inv.invoice_number}`, 14, 42);
  doc.setFont("helvetica", "normal"); doc.setFontSize(9);
  doc.text(`Date d'émission : ${new Date(inv.issued_at).toLocaleDateString("fr-FR")}`, 14, 48);
  doc.text(`Réf. cours : ${new Date(inv.booking.start_dt).toLocaleString("fr-FR")}`, 14, 53);
  // Émetteur = MySwimmingLessons SAS
  doc.setFont("helvetica", "bold"); doc.text("ÉMETTEUR", 14, 64);
  doc.setFont("helvetica", "normal");
  doc.text(`${p.name}`, 14, 70);
  doc.text(`${p.address_line1}`, 14, 75);
  doc.text(`${p.postal_code} ${p.city}`, 14, 80);
  doc.text(`${p.email}`, 14, 85);
  doc.text(`SIRET : ${p.siret}`, 14, 90);
  doc.text(`TVA : ${p.tva}`, 14, 95);
  // Destinataire client
  doc.setFont("helvetica", "bold"); doc.text("DESTINATAIRE (Client)", 110, 64);
  doc.setFont("helvetica", "normal");
  doc.text(`${inv.client?.name || ""}`, 110, 70);
  doc.text(`${cs.address || ""}`, 110, 75);
  doc.text(`${cs.postal_code || ""} ${cs.city || ""}`.trim(), 110, 80);
  doc.text(`${inv.client?.email || ""}`, 110, 85);
  doc.text(`${cs.phone || inv.client?.phone || ""}`, 110, 90);
  // Détail
  let y = 110;
  doc.setDrawColor(200); doc.setFillColor(240, 249, 255);
  doc.rect(14, y - 5, 182, 9, "F");
  doc.setFont("helvetica", "bold");
  doc.text("Désignation", 16, y);
  doc.text("HT", 132, y);
  doc.text("TVA (20%)", 152, y);
  doc.text("TTC", 175, y);
  doc.setFont("helvetica", "normal");
  y += 9;
  doc.text("Frais de service MySwimmingLessons (10%)", 16, y);
  doc.text(`${a.commission_ht.toFixed(2)} €`, 132, y);
  doc.text(`${a.commission_tva.toFixed(2)} €`, 152, y);
  doc.text(`${a.commission_ttc.toFixed(2)} €`, 175, y);
  y += 10;
  doc.setFont("helvetica", "bold"); doc.setFontSize(11);
  doc.text(`Total HT :`, 140, y); doc.text(`${a.commission_ht.toFixed(2)} €`, 195, y, { align: "right" }); y += 6;
  doc.text(`TVA 20% :`, 140, y); doc.text(`${a.commission_tva.toFixed(2)} €`, 195, y, { align: "right" }); y += 6;
  doc.text(`Total TTC :`, 140, y); doc.text(`${a.commission_ttc.toFixed(2)} €`, 195, y, { align: "right" });
  doc.setFontSize(7); doc.setTextColor(100);
  y += 12;
  doc.text("Frais de service de la plateforme MySwimmingLessons.", 14, y); y += 3.5;
  doc.text("MySwimmingLessons SAS — RCS NANTERRE — TVA intracommunautaire FR74987859402.", 14, y);
  doc.save(`${inv.invoice_number}.pdf`);
}

const fmtTime = (iso) => new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
const fmtDateTime = (iso) => new Date(iso).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" });

/** Download the Factur-X hybrid PDF (PDF/A-3 + embedded CII XML EN 16931).
 *  Compliant with the French e-invoicing mandate starting Sept 2026. */
async function downloadFacturX(bookingId, isCommission = false) {
  try {
    const path = isCommission
      ? `/invoices/booking/${bookingId}/commission/factur-x`
      : `/invoices/booking/${bookingId}/factur-x`;
    const { data, headers } = await api.get(path, { responseType: "blob" });
    const blob = new Blob([data], { type: "application/pdf" });
    const dispo = headers["content-disposition"] || "";
    const m = dispo.match(/filename="([^"]+)"/);
    const filename = m ? m[1] : `facturx-${bookingId}.pdf`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
    toast.success("Factur-X téléchargée");
  } catch (e) {
    toast.error(e.response?.data?.detail || "Factur-X indisponible");
  }
}

const STATUS_COLOR = {
  confirmed: "bg-emerald-500", pending: "bg-amber-500", cancelled: "bg-slate-300",
  completed: "bg-sky-600", no_show: "bg-red-500",
};
const STATUS_LABEL = {
  confirmed: "Confirmé", pending: "En attente",
  cancelled: "Annulé", completed: "Effectué", no_show: "Absent",
};
// Iter45 — only show "paid" payment statuses; the technical raw values like
// `cash_commission_pending` were leaking into the UI as a label which is
// confusing and reveals internal state. We map the relevant ones to a
// user-friendly label and hide the rest entirely.
const PAYMENT_STATUS_LABEL = {
  paid: "Payé",
  cash_due: "À régler sur place",
  refunded: "Remboursé",
  abandoned: null,
  pending: null,
  cash_commission_pending: null,
};

function MonthCalendar({ bookings, onSelect }) {
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setDate(1); d.setHours(0, 0, 0, 0); return d; });
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const startWeekday = (first.getDay() + 6) % 7;
  const days = [];
  for (let i = 0; i < startWeekday; i++) days.push(null);
  for (let d = 1; d <= last.getDate(); d++) days.push(new Date(year, month, d));

  const byDay = useMemo(() => {
    const m = {};
    bookings.forEach((b) => {
      const d = new Date(b.start_dt);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      (m[key] ||= []).push(b);
    });
    return m;
  }, [bookings]);

  const today = new Date();
  return (
    <div className="card-soft p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <Button variant="ghost" size="icon" onClick={() => setCursor(new Date(year, month - 1, 1))} data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></Button>
        <h3 className="text-xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>{MONTHS_FR[month]} {year}</h3>
        <Button variant="ghost" size="icon" onClick={() => setCursor(new Date(year, month + 1, 1))} data-testid="cal-next"><ChevronRight className="w-4 h-4" /></Button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-xs text-slate-500 mb-2">
        {DAYS_FR.map((d) => <div key={d} className="text-center font-semibold py-1">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {days.map((d, i) => {
          if (!d) return <div key={`empty-${i}`} />;
          const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
          const items = byDay[key] || [];
          const isToday = d.toDateString() === today.toDateString();
          return (
            <button key={key} onClick={() => items.length && onSelect(items)}
              className={`min-h-[80px] sm:min-h-[100px] rounded-lg border text-left p-1.5 transition-colors ${isToday ? "border-sky-500 bg-sky-50" : "border-slate-200 bg-white hover:bg-slate-50"} ${items.length ? "cursor-pointer" : ""}`}
              data-testid={`cal-day-${d.getDate()}`}>
              <div className={`text-xs font-semibold ${isToday ? "text-sky-700" : "text-slate-700"}`}>{d.getDate()}</div>
              <div className="mt-1 space-y-0.5">
                {items.slice(0, 2).map((b) => (
                  <div key={b.booking_id} className={`text-[10px] sm:text-xs px-1.5 py-0.5 rounded text-white truncate ${STATUS_COLOR[b.status] || "bg-slate-400"}`}>
                    {fmtTime(b.start_dt)} {b.mns?.name?.split(" ")[0] || ""}
                  </div>
                ))}
                {items.length > 2 && <div className="text-[10px] text-slate-500">+{items.length - 2}</div>}
              </div>
            </button>
          );
        })}
      </div>
      <div className="mt-4 flex flex-wrap gap-3 text-xs text-slate-600">
        {Object.entries(STATUS_LABEL).map(([k, v]) => (
          <span key={k} className="flex items-center gap-1.5"><span className={`w-3 h-3 rounded ${STATUS_COLOR[k]}`} /> {v}</span>
        ))}
      </div>
    </div>
  );
}

export default function ClientDashboard() {
  const [bookings, setBookings] = useState([]);
  const [view, setView] = useState("calendar");
  const [selected, setSelected] = useState([]);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [reviewTarget, setReviewTarget] = useState(null);
  const [reviewedIds, setReviewedIds] = useState(new Set());

  const reload = async () => {
    const { data } = await api.get("/bookings/me");
    setBookings(data);
    // fetch reviews for completed bookings to know which are reviewed
    const reviewed = new Set();
    for (const b of data.filter((x) => x.status === "completed")) {
      try {
        const { data: revs } = await api.get(`/reviews/mns/${b.mns_id}`);
        if (revs.some((r) => r.booking_id === b.booking_id)) reviewed.add(b.booking_id);
      } catch (e) { console.warn("Reviews: fetch failed for MNS", b.mns_id, e?.response?.status); }
    }
    setReviewedIds(reviewed);
  };
  useEffect(() => { reload(); }, []);

  const doCancel = async () => {
    try {
      const { data } = await api.post(`/bookings/${cancelTarget.booking_id}/cancel`);
      const pc = data?.penalty_charge;
      if (pc?.charged) {
        toast.warning(`Réservation annulée — pénalité de ${pc.amount?.toFixed?.(2) || pc.amount}€ prélevée sur votre carte enregistrée.`);
      } else if (pc?.refunded) {
        toast.warning(`Réservation annulée — remboursement partiel de ${pc.amount?.toFixed?.(2) || pc.amount}€ (30 % conservés au titre de la pénalité tardive).`);
      } else if (pc && !pc.charged && !pc.refunded && pc.error) {
        toast.error(`Annulée, mais la pénalité de 30 % n'a pas pu être prélevée : ${pc.error}`);
      } else {
        toast.success("Réservation annulée");
      }
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setCancelTarget(null);
    }
  };

  const upcoming = bookings.filter((b) => new Date(b.start_dt) >= new Date() && b.status !== "cancelled");
  const past = bookings.filter((b) => new Date(b.start_dt) < new Date() || b.status === "cancelled");

  const rowProps = { onCancel: setCancelTarget, onReview: setReviewTarget, reviewedIds };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
        <h1 className="text-3xl font-bold" style={{ fontFamily: "Outfit" }}>Mon planning</h1>
        <div className="flex gap-2 flex-wrap">
          <Button
            type="button"
            variant="outline"
            className="rounded-full border-2 border-sky-200 text-sky-700 hover:bg-sky-50"
            onClick={() => {
              const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
              window.location.href = `${apiBase}/api/bookings/me/calendar.ics`;
            }}
            data-testid="cta-sync-calendar"
            title="Télécharger tous vos cours au format .ics (Google, Apple, Outlook). Rappels 24h et 1h inclus."
          >
            <Calendar className="w-4 h-4 mr-1" />Ajouter à mon agenda
          </Button>
          <Link to="/carnet" data-testid="cta-carnet">
            <Button variant="outline" className="rounded-full border-2 border-sky-200 text-sky-700 hover:bg-sky-50">
              <FileText className="w-4 h-4 mr-1" />Mon carnet de progression
            </Button>
          </Link>
        </div>
      </div>
      <p className="text-slate-600 mb-6">Vue mensuelle de vos réservations.</p>

      <Tabs value={view} onValueChange={setView}>
        <TabsList className="bg-slate-100 p-1 rounded-xl mb-4">
          <TabsTrigger value="calendar" data-testid="tab-cal"><Calendar className="w-4 h-4 mr-1" />Calendrier</TabsTrigger>
          <TabsTrigger value="list" data-testid="tab-list">Liste</TabsTrigger>
        </TabsList>
        <TabsContent value="calendar">
          <MonthCalendar bookings={bookings} onSelect={setSelected} />
          {selected.length > 0 && (
            <div className="mt-4 card-soft p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold" style={{ fontFamily: "Outfit" }}>
                  Réservations du {new Date(selected[0].start_dt).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}
                </h3>
                <Button variant="ghost" size="icon" onClick={() => setSelected([])}><X className="w-4 h-4" /></Button>
              </div>
              <div className="space-y-2">
                {selected.map((b) => <BookingRow key={b.booking_id} b={b} {...rowProps} />)}
              </div>
            </div>
          )}
        </TabsContent>
        <TabsContent value="list">
          <div data-testid="client-bookings">
            {upcoming.length > 0 && (
              <>
                <h3 className="text-sm uppercase tracking-widest text-slate-500 mb-3">À venir</h3>
                <div className="grid gap-3 mb-6">{upcoming.map((b) => <BookingRow key={b.booking_id} b={b} {...rowProps} />)}</div>
              </>
            )}
            {past.length > 0 && (
              <>
                <h3 className="text-sm uppercase tracking-widest text-slate-500 mb-3">Historique</h3>
                <div className="grid gap-3">{past.map((b) => <BookingRow key={b.booking_id} b={b} {...rowProps} />)}</div>
              </>
            )}
            {bookings.length === 0 && <div className="card-soft p-8 text-center text-slate-500">Aucune réservation. <a href="/map" className="text-sky-700 font-medium">Trouver un MNS →</a></div>}
          </div>
        </TabsContent>
      </Tabs>

      <div className="mt-8">
        <LegalLinksCard />
      </div>

      <Dialog open={!!cancelTarget} onOpenChange={(o) => !o && setCancelTarget(null)}>
        <DialogContent data-testid="cancel-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="w-5 h-5" />
              Confirmer l'annulation
            </DialogTitle>
            <DialogDescription className="text-slate-700 pt-2">
              <strong>Attention</strong>, toute annulation de votre fait moins de 24h avant le début du cours entraînera une <strong>pénalité de 30 %</strong> du prix du cours.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" className="rounded-full" onClick={() => setCancelTarget(null)} data-testid="cancel-keep">Garder ma réservation</Button>
            <Button className="rounded-full bg-amber-600 hover:bg-amber-700" onClick={doCancel} data-testid="cancel-confirm">J'ai compris, annuler</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ReviewDialog target={reviewTarget} onClose={(submitted) => { setReviewTarget(null); if (submitted) reload(); }} />
    </div>
  );
}

function ReviewDialog({ target, onClose }) {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (target) { setRating(5); setComment(""); } }, [target]);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/reviews", { booking_id: target.booking_id, rating, comment });
      toast.success("Merci pour votre avis !");
      onClose(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent data-testid="review-dialog">
        <DialogHeader>
          <DialogTitle>Noter votre cours avec {target?.mns?.name}</DialogTitle>
          <DialogDescription>Donnez une note de 1 à 5 étoiles. Le commentaire est facultatif.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-center gap-2 py-4">
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} onClick={() => setRating(n)} data-testid={`star-${n}`}
              className="transition-transform hover:scale-110">
              <Star className={`w-10 h-10 ${n <= rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
            </button>
          ))}
        </div>
        <Textarea placeholder="Votre commentaire (facultatif)" value={comment} onChange={(e) => setComment(e.target.value)} data-testid="review-comment" />
        <DialogFooter className="flex gap-2">
          <Button variant="outline" className="rounded-full" onClick={() => onClose(false)} data-testid="review-skip">Plus tard</Button>
          <Button onClick={submit} disabled={busy} className="rounded-full bg-sky-700 hover:bg-sky-600" data-testid="review-submit">
            {busy ? "..." : "Publier l'avis"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BookingRow({ b, onCancel, onReview, reviewedIds }) {
  const confirm = useConfirm();
  const [invAvail, setInvAvail] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get(`/invoices/booking/${b.booking_id}/availability`)
      .then((r) => { if (!cancelled) setInvAvail(r.data); })
      .catch(() => { if (!cancelled) setInvAvail({ available: false, reason: "error" }); });
    return () => { cancelled = true; };
  }, [b.booking_id]);

  const askCancel = async () => {
    const start = new Date(b.start_dt);
    const within24h = (start - new Date()) < 24 * 3600 * 1000;
    if (within24h) {
      onCancel(b);
    } else {
      const ok = await confirm({
        title: "Annuler la réservation ?",
        body: "Vous êtes sur le point d'annuler cette réservation. Le créneau redeviendra disponible et aucune pénalité ne sera appliquée (annulation > 24h avant le cours).",
        confirmLabel: "Confirmer l'annulation",
        cancelLabel: "Garder",
        tone: "warn",
      });
      if (ok) {
        try { await api.post(`/bookings/${b.booking_id}/cancel`); toast.success("Annulé"); window.location.reload(); }
        catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
      }
    }
  };
  const canReview = b.status === "completed" && !reviewedIds.has(b.booking_id);
  const alreadyReviewed = b.status === "completed" && reviewedIds.has(b.booking_id);

  // Invoice availability rendering
  const showInvoice = invAvail?.available;
  const pendingValidation = invAvail?.reason === "pending_validation";

  return (
    <div className="card-soft p-4 sm:p-5">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`w-2.5 h-2.5 rounded-full ${STATUS_COLOR[b.status] || "bg-slate-300"}`} />
            <div className="font-semibold text-slate-900">{fmtDateTime(b.start_dt)}</div>
          </div>
          <div className="text-sm text-slate-600 mt-1 flex items-center gap-3 flex-wrap">
            <span>
              avec{" "}
              {b.mns_id ? (
                <Link
                  to={`/mns/${b.mns_id}`}
                  className="font-semibold text-sky-700 hover:underline"
                  data-testid={`bk-mns-link-${b.booking_id}`}
                >
                  {b.mns?.name}
                </Link>
              ) : (
                <strong>{b.mns?.name}</strong>
              )}
            </span>
            <span className="flex items-center gap-1 text-slate-500"><MapPin className="w-3 h-3" />{b.pool?.name}</span>
            <span className="flex items-center gap-1 text-slate-500"><Clock className="w-3 h-3" />{b.hours}h</span>
          </div>
          <div className="mt-2 flex gap-2 flex-wrap">
            <span className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-700">{STATUS_LABEL[b.status] || b.status}</span>
            <span className="text-xs px-2 py-1 rounded-full bg-sky-100 text-sky-700">{b.payment_method === "online" ? "En ligne" : "Espèces"}</span>
            {(() => {
              const label = PAYMENT_STATUS_LABEL[b.payment_status];
              if (label === undefined) {
                // Unknown payment_status — show as-is (defensive)
                return <span className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-700">{b.payment_status}</span>;
              }
              if (label === null) return null;  // hide pending / abandoned / cash_commission_pending
              return <span className="text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-700" data-testid={`pay-status-${b.booking_id}`}>{label}</span>;
            })()}
            {alreadyReviewed && <span className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-800">⭐ Avis publié</span>}
          </div>
          {/* Iter45 — pending payment within the 30-min Stripe window: surface a
              clear "Finaliser le paiement" CTA so the client can resume Stripe.
              Iter47 — clicking calls /resume-checkout which returns the original
              Stripe URL (cart is empty after checkout, can't go through /cart). */}
          {b.status === "pending" && ["pending", "cash_commission_pending"].includes(b.payment_status) && (
            <div className="mt-2 bg-amber-50 border border-amber-200 rounded-xl p-2 text-xs text-amber-800 flex items-start gap-2" data-testid={`bk-payment-pending-${b.booking_id}`}>
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>
                Paiement non finalisé.
                <button
                  type="button"
                  className="ml-1 underline font-medium hover:text-amber-900"
                  data-testid={`resume-payment-${b.booking_id}`}
                  onClick={async () => {
                    try {
                      const { data } = await api.get(`/bookings/${b.booking_id}/resume-checkout`);
                      if (data?.url) {
                        window.location.href = data.url;
                      } else {
                        toast.info("Lien de paiement indisponible — ré-ajoutez le créneau au panier.");
                      }
                    } catch (e) {
                      const detail = e.response?.data?.detail || "Erreur lors de la reprise du paiement.";
                      toast.warning(detail);
                    }
                  }}
                >
                  Reprendre le paiement
                </button>
                {" — sans paiement, votre réservation sera annulée automatiquement sous 30 min."}
              </span>
            </div>
          )}
          {pendingValidation && (
            <div className="mt-2 bg-amber-50 border border-amber-200 rounded-xl p-2 text-xs text-amber-800 flex items-start gap-2" data-testid={`bk-pending-validation-${b.booking_id}`}>
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>
                Le maître-nageur n'a pas encore validé la présence à ce cours. Il a été averti.
                Vous pouvez le contacter via la <Link to={`/messages/${b.mns_id}`} className="underline font-medium">messagerie</Link>.
              </span>
            </div>
          )}
        </div>
        <div className="text-right">
          <div className="text-xl font-bold text-sky-700">{b.amount}€</div>
          <div className="flex gap-2 mt-2 justify-end flex-wrap">
            {canReview && (
              <Button size="sm" className="rounded-full bg-amber-500 hover:bg-amber-600" onClick={() => onReview(b)} data-testid={`review-${b.booking_id}`}>
                <Star className="w-3 h-3 mr-1" />Laisser un avis
              </Button>
            )}
            {showInvoice && (
              <>
                <Button size="sm" variant="outline" onClick={() => generateInvoicePDF(b.booking_id)} data-testid={`invoice-${b.booking_id}`}>
                  <FileText className="w-3 h-3 mr-1" />Facture
                </Button>
                <Button size="sm" variant="outline" onClick={() => generateCommissionInvoicePDF(b.booking_id)} data-testid={`invoice-commission-${b.booking_id}`}>
                  <Receipt className="w-3 h-3 mr-1" />Frais service
                </Button>
                <Button size="sm" variant="outline" className="border-emerald-500 text-emerald-700 hover:bg-emerald-50" onClick={() => downloadFacturX(b.booking_id, false)} data-testid={`facturx-${b.booking_id}`} aria-label="Télécharger la facture Factur-X" title="Facture électronique conforme Factur-X (sept. 2026)">
                  <FileText className="w-3 h-3 mr-1" />Factur-X
                </Button>
              </>
            )}
            {b.status !== "cancelled" && b.status !== "completed" && b.status !== "no_show" && <Button size="sm" variant="outline" onClick={askCancel} data-testid={`cancel-${b.booking_id}`}><X className="w-3 h-3 mr-1" />Annuler</Button>}
            {/* Iter37 Vague 3: client validates MNS presence (symmetric to MNS validation) */}
            {(() => {
              const past = new Date(b.start_dt) < new Date();
              const eligible = past && (b.status === "pending" || b.status === "confirmed") && !b.client_presence_validated;
              if (!eligible) return null;
              const validate = async () => {
                try {
                  await api.post(`/bookings/${b.booking_id}/client-validate-presence`);
                  toast.success("Présence du maître-nageur confirmée. Merci !");
                  window.location.reload();
                } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
              };
              return (
                <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={validate} data-testid={`client-validate-presence-${b.booking_id}`}>
                  <Star className="w-3 h-3 mr-1" />Confirmer la présence du MNS
                </Button>
              );
            })()}
            {b.client_presence_validated && (
              <span className="text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-700" data-testid={`client-presence-confirmed-${b.booking_id}`}>✓ Présence confirmée</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
